<template>
  <div class="container-fluid">
    <!-- Breadcrumb -->
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Internal User Management</h2>
          </div>

          <!-- Body -->
          <div class="body pt-0">
            <!-- Search and Filters -->
            <div
              class="chart-top-action d-lg-flex justify-content-between py-3"
            >
              <!-- Search Box -->
              <div class="mr-0 mr-lg-3 action-top-search">
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search by name, email, or phone"
                    aria-label="Search"
                    v-model="searchKeywords"
                    v-on:keyup.enter="searchUsers"
                    aria-describedby="search-users"
                  />
                  <div class="input-group-append" @click="searchUsers()">
                    <span class="input-group-text" id="search-users">
                      <i class="icon-magnifier"></i>
                    </span>
                  </div>
                </div>
              </div>
              <!-- Create User Button -->
              <template v-if="$store.getters['authpermission/hasAnyPermission'](['users.create'])">
                <div class="ml-auto">
                  <b-button variant="primary" @click="showCreateUserModal">
                    <i class="fa fa-plus"></i>
                    Create User
                  </b-button>
                </div>
              </template>
            </div>

            <!-- Users Table -->
            <b-table
              responsive
              table-class="mb-0 table-custom border"
              head-variant="light"
              :items="items"
              :fields="fields"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
              striped
              show-empty
            >
              <template #head()="{ label, field: { key } }">
                {{ label }}
                <template>
                  <i v-if="sortBy !== key" class=""></i>
                  <i
                    v-else-if="sortDesc"
                    class="fa fa-long-arrow-down ml-1"
                  ></i>
                  <i v-else class="fa fa-long-arrow-up ml-1"></i>
                </template>
              </template>

              <template #cell(name)="data">
                <span class="font-weight-bold">
                  {{ data.item.firstname }} {{ data.item.lastname }}
                </span>
              </template>

              <template #cell(status)="data">
                <p class="function-badge badge-group-table mb-0 text-center">
                  <span
                    class="badge ml-0 d-block"
                    :class="{
                      'badge-success':
                        data.value && data.value.toLowerCase() === 'active',
                      'badge-warning':
                        data.value &&
                        (data.value.toLowerCase() === 'inactive' ||
                          data.value.toLowerCase() === 'changepassword'),
                      'badge-danger':
                        data.value && data.value.toLowerCase() === 'locked',
                    }"
                  >
                    {{ data.value }}
                  </span>
                </p>
              </template>

              <template #cell(isLockedOut)="data">
                <i v-if="data.value" class="fa fa-lock text-danger"></i>
                <i v-else class="fa fa-unlock text-success"></i>
              </template>
            </b-table>

            <!-- Table Pagination Bottom -->
            <div
              class="d-flex py-3 align-items-center justify-content-between table-pagination"
            >
              <div class="per-page">
                <b-form-group
                  label="Per page"
                  label-for="per-page-select"
                  class="mb-0"
                >
                  <b-form-select
                    id="per-page-select"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="number-page">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
              <div class="go-page">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">Go To Page</span>
                  </div>
                  <input
                    type="text"
                    v-model="currentPageJump"
                    class="form-control"
                    @keyup.enter="onEnter"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <create-user-modal
      ref="createUserModal"
      @user-created="onUserCreated"
    ></create-user-modal>

    <!-- Loader -->
    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { InternalUserService } from "@/services/internalUserService";
import CreateUserModal from "./CreateUserModal.vue";

const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "UserListComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
    "create-user-modal": CreateUserModal,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Home", links: "/home" },
        { title: "User Management", isbold: true },
      ],
      fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "phone", label: "Phone", sortable: true },
        { key: "role", label: "Role", sortable: true },
        { key: "accessright", label: "Access Right", sortable: true },
        { key: "function", label: "Function", sortable: true },
        { key: "countryname", label: "Country", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "isLockedOut", label: "Locked", sortable: true },
      ],
      items: [],
      currentPage: 1,
      perPage: 25,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      sortBy: "firstname",
      sortDesc: false,
      searchKeywords: "",
      currentPageJump: "",
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("internalUser", ["users", "totalUsers", "loading"]),
  },
  watch: {
    currentPage() {
      this.fetchUsers();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchUsers();
    },
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    ...mapActions("internalUser", [
      "setUsers",
      "setTotalUsers",
      "setLoading",
      "setCurrentPage",
      "setPerPage",
      "setSort",
      "setSortBy",
      "setSearchKeyword",
    ]),

    async fetchUsers() {
      this.isLoading = true;
      this.setLoading(true);

      try {
        const params = {
          page: this.currentPage,
          size: this.perPage,
          sort: this.sortDesc ? "desc" : "asc",
          sortby: this.sortBy,
        };

        if (this.searchKeywords && this.searchKeywords.trim() !== "") {
          params.search = this.searchKeywords.trim();
        }

        const response = await InternalUserService.getUsers(params);

        if (response.data && response.data.details) {
          this.items = response.data.details.map((user) => ({
            userid: user.userid,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            phone: user.phone,
            isPhoneConfirmed: user.isPhoneConfirmed,
            twofactorenabled: user.twofactorenabled,
            createdby: user.createdby,
            status: user.status,
            isLockedOut: user.isLockedOut,
            role: user.role,
            accessright: user.accessright,
            function: user.function,
            countryid: user.countryid,
            countryname: user.countryname,
            isocode: user.isocode,
          }));

          this.totalRows = response.data.query.total;
          this.setUsers(this.items);
          this.setTotalUsers(this.totalRows);
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to fetch users. Please try again.",
          { position: "top-right" },
        );
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
        this.setLoading(false);
      }
    },

    searchUsers() {
      this.currentPage = 1;
      this.fetchUsers();
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchUsers();
    },

    onEnter() {
      const pageNumber = parseInt(this.currentPageJump);
      const maxPages = Math.ceil(this.totalRows / this.perPage);

      if (pageNumber && pageNumber > 0 && pageNumber <= maxPages) {
        this.currentPage = pageNumber;
        this.currentPageJump = "";
      } else {
        this.$toast.warning(
          `Please enter a valid page number between 1 and ${maxPages}`,
          { position: "top-right" },
        );
      }
    },

    showCreateUserModal() {
      this.$refs.createUserModal.show();
    },

    onUserCreated() {
      this.fetchUsers();
    },
  },
};
</script>

<style scoped>
.card-top-line {
  border-top: 3px solid #007bff;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}

.table-custom {
  font-size: 0.9rem;
}

.action-top-search {
  min-width: 300px;
}
</style>
